import { Description, Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { useEffect, useState } from 'react';

import { Icon, type IconName } from './icons.tsx';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  icon?: IconName;
  /** danger = red confirm button for destructive actions (log out, delete). */
  tone?: 'danger' | 'neutral';
  busy?: boolean;
  /** For what can't be undone: the confirm button waits until this exact text is typed
   *  (case and extra spaces ignored), e.g. a portfolio's name before deleting all its data. */
  typeToConfirm?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * In-app confirmation (replaces window.confirm), built on Headless UI's Dialog: focus is
 * trapped, Esc and a tap on the backdrop cancel, and screen readers announce it as a modal.
 * A centred card on every screen size (16 px side margin on phones, max 448 px wide).
 * Cancel gets focus first, so an accidental Enter never confirms.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  icon = 'alert',
  tone = 'danger',
  busy = false,
  typeToConfirm,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  useEffect(() => {
    if (open) setTyped('');
  }, [open, typeToConfirm]);
  const tidy = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
  const matches = !typeToConfirm || tidy(typed) === tidy(typeToConfirm);
  return (
    <Dialog open={open} onClose={onCancel} className="relative z-50">
      <DialogBackdrop transition className="fixed inset-0 bg-ink/55 transition duration-150 ease-out data-closed:opacity-0" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel
          transition
          className="w-full max-w-md rounded-2xl bg-paper text-ink shadow-[0_20px_48px_rgba(19,33,31,0.28)] transition duration-150 ease-out data-closed:scale-95 data-closed:opacity-0"
        >
          <div className="flex flex-col gap-4 px-5 pt-6 pb-5">
            <div className="flex items-start gap-3.5">
              <span
                className={`flex size-12 shrink-0 items-center justify-center rounded-full ${
                  tone === 'danger' ? 'bg-danger-tint text-danger' : 'bg-ground text-ink'
                }`}
              >
                <Icon name={icon} size={24} />
              </span>
              <div className="flex min-w-0 flex-col gap-1 pt-0.5">
                <DialogTitle className="m-0 text-xl leading-snug font-bold rtl:text-[22px]">{title}</DialogTitle>
                <Description className="m-0 text-[15px] leading-relaxed text-muted rtl:text-[17px]">{body}</Description>
              </div>
            </div>
            {typeToConfirm && (
              <label className="flex flex-col gap-1.5 text-sm font-bold">
                <span>
                  Type <span className="rounded bg-ground px-1.5 py-0.5 font-bold">{typeToConfirm}</span> to confirm
                </span>
                <input
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  className="h-11 rounded-lg border-[1.5px] border-line-strong bg-paper px-3 text-[15px] font-normal focus-visible:border-ink"
                />
              </label>
            )}
            <div className="grid grid-cols-2 gap-3 pt-1">
              <button
                type="button"
                data-autofocus
                onClick={onCancel}
                className="h-14 rounded-[14px] border-[1.5px] border-line-strong bg-paper text-base font-bold hover:bg-ground focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ink"
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                disabled={busy || !matches}
                onClick={onConfirm}
                className={`h-14 rounded-[14px] text-base font-bold text-white focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ink disabled:opacity-60 ${
                  tone === 'danger' ? 'bg-danger hover:bg-[#991B1B]' : 'bg-brand hover:bg-brand-dark'
                }`}
              >
                {confirmLabel}
              </button>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
